
// Vue
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";

// Types
import { GroupWithPermissions } from "@/store/areas/types"
import { ISyncSystem, IServerTypes, IQuerySync } from "@/store/sync/types";
import { VueBootstrapField } from "@/store/types"
import {
	UserManagementState,
	UserDetails,
	UserRole,
	UserRolePermission,
    Permission

} from "@/store/user-management/types";

// Third Party
import { validationMixin } from "vuelidate";
import { required, requiredIf, minLength, maxLength, numeric } from "vuelidate/lib/validators";
import { isEqual, debounce } from "lodash";

// Components
import SureViewIcon from    "@/components/SureViewIcon.vue"
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import vSelect from "vue-select";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

// Namespaces
const Areas = namespace("areas");
const Sync = namespace("sync");

@Component({
    mixins: [validationMixin],
    validations: {
        currentSystemSync: {
            title: {
                required,
                minLength: minLength(3),
                maxLength: maxLength(50)
            },
            groupId: {
                required,
                minValue: (value) => value > 0
            },
            serverTypeId: {
                required,
                minValue: (value) => value > 0
            },
            username: {
                maxLength: maxLength(50)
            },
            password: {
                maxLength: maxLength(50)
            },
            host: {
                maxLength: maxLength(100)
            },
            port: {
                maxLength: maxLength(50)
            },
            extraValue: {
                maxLength: maxLength(500)
            }
        },
        hasDataChanged: {
            required: requiredIf(function() {
                const result = this.syncSystem.find(x => x.syncSystemId == this.currentSystemSync.syncSystemId);
                // isEqual can be pretty tricky. Make sure you pass by value, not reference when comparing.
                // So one should be careful using this function.
                return isEqual(result, this.currentSystemSync);
            })
        }
    },
	components: {
        "area-tree-select": AreaTreeSelect,
        "v-select": vSelect,
        "vue-perfect-scrollbar": VuePerfectScrollbar,
        "sureview-icon": SureViewIcon,
	}
})
export default class SyncSystemSetup extends Vue {
	@Areas.Action fetchAreaDictionary: () => Promise<void>;
	@Areas.Getter getAreaTitle: (groupId: number) => string;

    @Sync.Action getSyncSystems: (page: number) => Promise<void>;
    @Sync.Action getServerTypesWithCanSync: () => Promise<void>;
    @Sync.Action updateSyncSystem: (syncSystem: ISyncSystem) =>  Promise<void>;
    @Sync.Action saveNewSyncSystem: (syncSystem: ISyncSystem) =>  Promise<void>;
    @Sync.Action deleteSyncSystem: (syncSystemId: number) => Promise<void>;
    @Sync.Action querySyncSystem: (query: IQuerySync) => Promise<void>;

    @Sync.Mutation setSyncSystemFilter: (filter: string) => void;
    @Sync.Mutation resetQueriedSyncSystems: () => void;
    @Sync.Mutation setSyncSystemsAtEndStatus: (endStatus: boolean) => void;
    @Sync.Mutation setSyncSystemFilteredPageIndex: (pageIndex: number) => void;
    @Sync.Getter getServerTypeTitleById: (serverTypeId: number) => string;
    @Sync.Getter getSyncSystemDefaultOrFiltered: ISyncSystem[];

    @Sync.State syncSystemTemplate: ISyncSystem;
    @Sync.State syncSystem: ISyncSystem[];
    @Sync.State syncSystemsIsBusy: boolean;
    @Sync.State syncSystemPageIndex: number;
    @Sync.State serverTypesCanSync: IServerTypes[];
    @Sync.State syncSystemsAtEnd: boolean;
    @Sync.State syncSystemPageSize: number;
    @Sync.State syncSystemFilter: string;
    @Sync.State syncSystemFiltered: ISyncSystem[];
    @Sync.State syncSystemFilteredPageIndex: number;

    @Prop({ default: false, type: Boolean }) public visable;

    private term: string = "Sync System";
    private filter: string = "";
    private SiteSyncModal: boolean = false;
    private isEditing: boolean = false;
    private loadingContent: boolean = false;

    private fields: VueBootstrapField[] = [
        { key: 'title', label: 'Title', sortable: true  },
        { key: 'serverTypeId', label: 'Server Type Name', sortable: false, formatter: (serverTypeId, key, item) => { return serverTypeId > 0 ? this.getServerTypeTitle(serverTypeId) : 'N/A' } },
        { key: 'groupId', label: 'Area', sortable: true, formatter: (groupId, key, item) => { return groupId > 0 ? this.getAreaTitleById(groupId) : 'N/A' } },
        { key: 'host', label: 'Host', sortable: true  },
        { key: 'port', label: 'Port', sortable: true  },
        { key: 'extraValue', label: 'Extra Value', sortable: true, sortDirection: 'desc', class: 'column-vuebootstrap-table' },
        { key: 'syncEntriesCount', label: 'Sync Entries', sortable: false },
		{ key: 'actions', label: 'Actions', sortable: false }
    ];


	public async mounted(): Promise<void> {
        await this.fetchAreaDictionary();
        await this.getServerTypesWithCanSync();
        this.setSyncSystemsAtEndStatus(false);
    }

    /** MODAL Helper: Generate title. */
    private get ModalTitle(): string {
        return this.isEditing ? `Editing: ${this.currentSystemSync.title}` : "Add new Sync System"
    }

    private getAreaTitleById(groupId: number): string {
        return this.getAreaTitle(groupId);
    }

    private getServerTypeTitle(serverTypeId: number): string {
        return this.getServerTypeTitleById(serverTypeId);
    }

	private openModalNew(): void {
        // Clears data that is linked with the modal.
        this.currentSystemSync = Object.assign({}, this.syncSystemTemplate);

        this.SiteSyncModal = true;
        this.isEditing = false;
	}

	private openRowModalEdit(item: ISyncSystem, index: number, event: any): void {
        // Clears data that is linked with the modal.
        this.currentSystemSync = Object.assign({}, this.syncSystemTemplate);

        // We want to pass our object by value, not reference.
        this.currentSystemSync = Object.assign({}, item);
        this.SiteSyncModal = true;
        this.isEditing = true;
	}

	private async rowModalDelete(): Promise<void> {
        await this.deleteSyncSystem(this.currentSystemSync.syncSystemId);
        this.SiteSyncModal = false;
	}

	private async rowModalSave(): Promise<void> {
        if (this.isEditing) {
            await this.updateSyncSystem(this.currentSystemSync);
        } else {
            await this.saveNewSyncSystem(this.currentSystemSync);
        }

        this.SiteSyncModal = false;

        // Clears data that is linked with the modal.
        this.currentSystemSync = Object.assign({}, this.syncSystemTemplate);
    }

	private currentSystemSync: ISyncSystem = {
        extraValue: "",
        groupId: 0,
        host: "",
        password: "",
        port: 0,
        serverTypeId: null,
        syncSystemId: 0,
        title: "",
        username: "",
        syncEntriesCount: 0
	}

    /** scrollbar at bottom of Y axis listener */
    private async psHandlerReachEnd(event: any): Promise<void> {
        if (!this.visable) return;

        // First check if we got any content in our list.
        if (!this.getSyncSystemDefaultOrFiltered || this.getSyncSystemDefaultOrFiltered.length == 0) return;

        if (this.syncSystemsIsBusy) return;
        if (this.syncSystemsAtEnd) return;
        if (this.loadingContent) return;
        if (this.syncSystem.length < this.syncSystemPageSize) return;

        // Lets check we are querying or just getting the next load of data.
        if (this.filter !== "") {
            if (this.syncSystemFiltered.length == 0) return;
            const payload: IQuerySync = {
                index: this.syncSystemFilteredPageIndex + 1,
                pageSize:  this.syncSystemPageSize,
                filter: this.filter
            }

            this.loadingContent = true;
            await this.querySyncSystem(payload);
            this.loadingContent = false;
            return;
        }

        this.loadingContent = true;
        await this.getSyncSystems(this.syncSystemPageIndex + 1);
        this.loadingContent = false;
    }

    /** Helper method to convert large numbers to more basic representation 5000 = 5k 4490 = 4.5k */
    private kFormatter(num: number) {
        return Math.abs(num) > 999 ? Math.sign(num) * (+(Math.abs(num)/1000).toFixed(1)) + 'k' : Math.sign(num)*Math.abs(num)
    }

    private async searchSyncSystem() {
        if (!this.filter || this.filter === "") {
            this.setSyncSystemFilteredPageIndex(1);
            this.setSyncSystemsAtEndStatus(false);
            this.resetQueriedSyncSystems();
        }

        // Slice filter to be only 250 characters
        this.filter = this.filter.slice(0, 249);

        this.setSyncSystemFilter(this.filter);
        const payload: IQuerySync = {
            index: this.syncSystemFilteredPageIndex,
            pageSize:  this.syncSystemPageSize,
            filter: this.filter
        }
        await this.querySyncSystem(payload);
    }

    private debouncedSyncSytemQuery = debounce(async () => {
        await this.searchSyncSystem();
    }, 1000);
}
